import React, { useState } from 'react';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { Tabs, Tab, Typography, Box, CircularProgress, useTheme } from "@mui/material";
import { useGetAllClaimsQuery } from "store/slices/apis/claimsPackageSlice";
import { useGetAllChangeQuery } from "store/slices/apis/changeTrackerPackageSlice";
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';

type Person = {
    claim_number: string;
    package: string;
    sub_package: string;
    approved_status: string;
    status: string;
    createddate: string;
    contract_number: string;
    consultant_contractor: string;
    claim_raised_to: string;
    claim_received_on: string;
    claim_raised_by_refrence: string;
    claim_raised_by_date_received: string;
    claim_raised_to_reference: string;
    claim_raised_to_date_of_response: string;
    claim_raised_to_position: string;
    claim_raised_by_estimate_cost_impact: string;
    claim_raised_to_estimate_cost_impact: string;
    approved_cost_impact: string;
    claim_raised_by_estimate_time_impact: string;
    claim_raised_to_estimate_time_impact: string;
    approved_time_impact: string;
    actual_date_of_closure: string;
};

type Other = {
    change_number: string;
    package: string;
    sub_package1: string;
    approved_status: string;
    status: string;
    createddate: string;
    contractor_number: string;
    consultant_contractor: string;
    change_raised_to: string;
    change_received_on: string;
    change_raised_by_refrence: string;
    change_raised_by_date_received: string;
    change_raised_to_date_of_response: string;
    change_raised_to_position: string;
    approved_cost_impact: string;
    change_raised_by_estimate_cost_impact: string;
    change_raised_to_estimate_cost_impact: string;
    change_raised_by_estimate_time_impact: string;
    change_raised_to_estimate_time_impact: string;
    approved_time_impact: string;
    actual_date_of_closure: string;
};

const columnsPerson: MRT_ColumnDef<Person>[] = [
    { accessorKey: 'claim_number', header: 'Claim ID', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'package', header: 'Category', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center', sx: { whiteSpace: 'nowrap' }, }, },
    { accessorKey: 'sub_package', header: 'Sub Category 1', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center', sx: { whiteSpace: 'nowrap' }, }, },
    { accessorKey: 'approved_status', header: 'Workflow Status', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'status', header: 'Current Status', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'createddate', header: 'Created Date', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'contract_number', header: 'Contract Number', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'consultant_contractor', header: 'Consultant/Contractor', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'claim_raised_to', header: 'Claim Raised To', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'claim_received_on', header: 'Claim Received On', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'claim_raised_by_refrence', header: 'Claim Raised By Reference', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center', sx: { whiteSpace: 'nowrap' }, }, },
    { accessorKey: 'claim_raised_by_date_received', header: 'Claim Raised By Date Received', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'claim_raised_to_reference', header: 'Claim Raised To Reference', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center', sx: { whiteSpace: 'nowrap' }, }, },
    { accessorKey: 'claim_raised_to_date_of_response', header: 'Claim Raised To Date Of Response', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'claim_raised_to_position', header: 'Claim Raised To Position', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center', sx: { whiteSpace: 'nowrap' }, }, },
    { accessorKey: 'claim_raised_by_estimate_cost_impact', header: 'Claim Raised By Estimate Cost Impact', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'claim_raised_to_estimate_cost_impact', header: 'Claim Raised To Estimate Cost Impact', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'approved_cost_impact', header: 'Approved Cost Impact', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'claim_raised_by_estimate_time_impact', header: 'Claim Raised By Estimate Time Impact', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'claim_raised_to_estimate_time_impact', header: 'Claim Raised To Estimate Time Impact', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'approved_time_impact', header: 'Approved Time Impact', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'actual_date_of_closure', header: 'Actual Date Of Closure', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
];

const columnsOther: MRT_ColumnDef<Other>[] = [
    { accessorKey: 'change_number', header: 'Change Order ID', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'package', header: 'Category', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center', sx: { whiteSpace: 'nowrap' }, }, },
    { accessorKey: 'sub_package1', header: 'Sub Category 1', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center', sx: { whiteSpace: 'nowrap' }, }, },
    { accessorKey: 'approved_status', header: 'Workflow Status', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'status', header: 'Current Status', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'createddate', header: 'Created Date', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'contractor_number', header: 'Contractor Number', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'consultant_contractor', header: 'Consultant/Contractor', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'change_raised_to', header: 'Change Raised To', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'change_received_on', header: 'Change Received On', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'change_raised_by_refrence', header: 'Change Raised By Reference', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center', sx: { whiteSpace: 'nowrap' }, }, },
    { accessorKey: 'change_raised_by_date_received', header: 'Change Raised By Date Received', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'change_raised_to_date_of_response', header: 'Change Raised To Date Of Response', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'change_raised_to_position', header: 'Change Raised To Position', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center', sx: { whiteSpace: 'nowrap' }, }, },
    { accessorKey: 'approved_cost_impact', header: 'Approved Cost Impact', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'change_raised_by_estimate_cost_impact', header: 'Change Raised By Estimate Cost Impact', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'change_raised_to_estimate_cost_impact', header: 'Change Raised To Estimate Cost Impact', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'change_raised_by_estimate_time_impact', header: 'Change Raised By Estimate Time Impact', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'change_raised_to_estimate_time_impact', header: 'Change Raised To Estimate Time Impact', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'approved_time_impact', header: 'Approved Time Impact', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
    { accessorKey: 'actual_date_of_closure', header: 'Actual Date Of Closure', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
];

// Format helper
const formatDate = (dateString?: string) =>
    dateString ? dayjs(dateString).format('DD/MM/YY') : '';

// Flatten formatter (no children)
const formatScheduleData = (data: any[]): Person[] =>
    data.map((item) => ({
        claim_number: item.claim_number,
        package: item.package,
        sub_package: item.sub_package,
        approved_status: item.approved_status,
        status: item.status,
        createddate: formatDate(item.createddate),
        contract_number: item.contract_number,
        consultant_contractor: item.consultant_contractor,
        claim_raised_to: item.claim_raised_to,
        claim_received_on: formatDate(item.claim_received_on),
        claim_raised_by_refrence: item.claim_raised_by_refrence,
        claim_raised_by_date_received: formatDate(item.claim_raised_by_date_received),
        claim_raised_to_reference: item.claim_raised_to_reference,
        claim_raised_to_date_of_response: formatDate(item.claim_raised_to_date_of_response),
        claim_raised_to_position: item.claim_raised_to_position,
        claim_raised_by_estimate_cost_impact: item.claim_raised_by_estimate_cost_impact,
        claim_raised_to_estimate_cost_impact: item.claim_raised_to_estimate_cost_impact,
        approved_cost_impact: item.approved_cost_impact,
        claim_raised_by_estimate_time_impact: item.claim_raised_by_estimate_time_impact,
        claim_raised_to_estimate_time_impact: item.claim_raised_to_estimate_time_impact,
        approved_time_impact: item.approved_time_impact,
        actual_date_of_closure: formatDate(item.actual_date_of_closure),
    }));

// Flatten formatter (no children)
const formatOtherData = (data: any[]): Other[] =>
    data.map((item) => ({
        change_number: item.change_number,
        package: item.package,
        sub_package1: item.sub_package1,
        approved_status: item.approved_status,
        status: item.status,
        createddate: formatDate(item.createddate),
        contractor_number: item.contractor_number,
        consultant_contractor: item.consultant_contractor,
        change_raised_to: item.change_raised_to,
        change_received_on: formatDate(item.change_received_on),
        change_raised_by_refrence: item.change_raised_by_refrence,
        change_raised_by_date_received: formatDate(item.change_raised_by_date_received),
        change_raised_to_date_of_response: formatDate(item.change_raised_to_date_of_response),
        change_raised_to_position: item.change_raised_to_position,
        approved_cost_impact: item.approved_cost_impact,
        change_raised_by_estimate_cost_impact: item.change_raised_by_estimate_cost_impact,
        change_raised_to_estimate_cost_impact: item.change_raised_to_estimate_cost_impact,
        change_raised_by_estimate_time_impact: item.change_raised_by_estimate_time_impact,
        change_raised_to_estimate_time_impact: item.change_raised_to_estimate_time_impact,
        approved_time_impact: item.approved_time_impact,
        actual_date_of_closure: formatDate(item.actual_date_of_closure),
    }));

export default function ClaimsChangeOrderTable() {

    const newtheme = useTheme();
    const { projectId } = useParams();
    const [tabIndex, setTabIndex] = useState(0);
    const currentTab = tabIndex === 0 ? "claims" : "changeOrders";

    const {
        data: allClaims,
        isLoading: isLoadingClaims,
        error: errorClaims,
    } = useGetAllClaimsQuery({
        ProjectId: projectId,
        sortBy: "createddate",
        sortOrder: "desc",
        approved_status: "Approved",
    });

    const {
        data: allChange,
        isLoading: isLoadingChangeOrders,
        error: errorChange,
    } = useGetAllChangeQuery({
        ProjectId: projectId,
        sortBy: "createddate",
        sortOrder: "desc",
        approved_status: "Approved",
    });

    const dataPerson: Person[] = allClaims?.data ? formatScheduleData(allClaims?.data) : [];

    const dataOther: Other[] = allChange?.data ? formatOtherData(allChange?.data) : [];

    const finalData = currentTab === "claims" ? dataPerson || [] : dataOther || [];

    const columns = currentTab === "claims" ? columnsPerson || [] : columnsOther || [];

    const isLoading = isLoadingClaims || isLoadingChangeOrders;
    const error = Boolean(errorClaims) || Boolean(errorChange);

    const otherColumnIds = columnsOther.map(c =>
        typeof c.accessorKey === 'string' ? c.accessorKey : ''
    );
    const [otherColumnOrder, setOtherColumnOrder] = useState(otherColumnIds);

    return (
        <Box sx={{
            width: "100%", border: "1px solid #D8D8D8", mt: "10px", borderRadius: "12px", overflow: "hidden",
        }}>
            <Box sx={{ backgroundColor: "#fff", p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: `${newtheme?.customSettings?.themeColor}`, fontSize: "18px" }}>
                    List of Claims and Change Order
                </Typography>
                <Typography sx={{ mb: 1, color: "#666" }}>
                    Track claim details and approval status to ensure transparency and timely resolution
                </Typography>

                <Tabs
                    value={tabIndex}
                    onChange={(_, newVal) => setTabIndex(newVal)}
                    sx={{
                        minHeight: 0,
                        paddingTop: "13px",
                        "& .MuiTabs-flexContainer": {
                            gap: "24px",
                        },
                        "& .MuiTabs-indicator": {
                            display: "flex",
                            justifyContent: "center",
                            backgroundColor: "transparent",
                            height: "2px",
                        },
                        "& .MuiTabs-indicator::after": {
                            content: '""',
                            display: "block",
                            height: "2px",
                            backgroundColor: newtheme?.customSettings?.themeColor || "#000",
                            width: "100%",
                            maxWidth: "100%",
                        },
                    }}
                >
                    <Tab
                        disableRipple
                        label="Claims"
                        sx={{
                            minHeight: "0 !important",
                            padding: "0 !important",
                            paddingBottom: "10px !important",
                            alignItems: "flex-start",
                            textTransform: "none",
                            fontSize: "14px",
                            color: "#696969",
                            minWidth: "auto",
                            "&.Mui-selected": {
                                color: newtheme?.customSettings?.themeColor || "#696969",
                                fontWeight: 500,
                            },
                        }}
                    />
                    <Tab
                        disableRipple
                        label="Change Orders"
                        sx={{
                            minHeight: "0 !important",
                            padding: "0 !important",
                            paddingBottom: "10px !important",
                            alignItems: "flex-start",
                            textTransform: "none",
                            fontSize: "14px",
                            color: "#696969",
                            minWidth: "auto",
                            "&.Mui-selected": {
                                color: newtheme?.customSettings?.themeColor || "#696969",
                                fontWeight: 500,
                            },
                        }}
                    />
                </Tabs>

            </Box>
            {isLoading ? (
                <Box display="flex" justifyContent="center" mt={4} pb={1}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Typography color="error">Failed to load data.</Typography>
            ) : (
                <MaterialReactTable
                    columns={columns}
                    data={finalData}
                    state={currentTab === 'changeOrders' ? { columnOrder: otherColumnOrder } : {}}
                    onColumnOrderChange={newOrder => {
                        if (currentTab === 'changeOrders') setOtherColumnOrder(newOrder);
                    }}
                    muiTableHeadCellProps={{
                        sx: {
                            fontWeight: 500,
                            fontSize: "14px",
                            color: "#505258",
                            padding: "10px 14px",
                            backgroundColor: "#F8F8F8",
                            border: "1px solid #D8D8D8",
                        },
                    }}
                    muiTableBodyRowProps={{
                        sx: {
                            fontWeight: 400,
                            fontSize: "14px",
                            color: "#505258",
                            padding: "10px 14px",
                            backgroundColor: "#FFFFFF",
                            border: "1px solid #D8D8D8",
                            opacity: "0.54"
                        },
                    }}
                    enableExpanding={false}
                    enableRowSelection={false}
                    enableFullScreenToggle={false}
                    //enableColumnOrdering={false}
                    enableColumnActions={false}
                />
            )}
        </Box>
    );
}